import React, { Component } from "react";
import axios from "axios";
import { Table, Button, Modal, Form, Row, Col, Badge } from "react-bootstrap";
import CartContext from "./CartContext";

export default class ProjectTasks extends Component {
  static contextType = CartContext;

  constructor(props) {
    super(props);
    this.state = {
      showModal: false,
      tasks: [],
      projects: [],
      employees: [],
      filteredTasks: [],
      showModal: false,
      currentTask: this.getEmptyTask(),
      filters: { projectId: "", assignedTo: "", status: "" },
      currentPage: 1,
      pageSize: 5,
      errors: {}, // 🔎 track validation errors
    };
  }

  getEmptyTask = () => ({
    id: null,
    projectId: "",
    storeId: "",
    created: new Date(),
    taskName: "",
    taskDescription: "",
    createdBy: "",
    assignedTo: "",
    startDate: "",
    endDate: "",
    estimatedHours: 0,
    actualHours: 0,
    status: "",
  });

  componentDidMount() {
    this.loadData();
  }

  // Replace your current loadData with this:
  loadData = () => {
    const base = process.env.REACT_APP_API;
    const { storeId } = this.context;

    this.setState({ loading: true, error: null });

    Promise.all([
      axios.get(`${base}Project/Store/${storeId}`),
      axios.get(`${base}Employee/GetEmployees/${storeId}`),
      axios.get(`${base}ProjectTask/Store/${storeId}`),
    ])
      .then(([projectsRes, employeesRes, tasksRes]) => {
        this.setState(
          {
            projects: projectsRes.data ?? [],
            employees: employeesRes.data ?? [],
            tasks: tasksRes.data ?? [],
            loading: false,
          },
          // re-apply current filters after data refresh
          this.applyFilters
        );
      })
      .catch(() =>
        this.setState({ loading: false, error: "Failed to load data." })
      );
  };

  handleFilterChange = (e) => {
    const { name, value } = e.target;
    this.setState(
      (prev) => ({
        filters: { ...prev.filters, [name]: value },
        currentPage: 1,
      }),
      this.applyFilters
    );
  };

  applyFilters = () => {
    const { tasks, filters } = this.state;
    let ft = tasks;
    if (filters.projectId)
      ft = ft.filter((t) => t.projectId === filters.projectId);
    if (filters.assignedTo)
      ft = ft.filter((t) => t.assignedTo === filters.assignedTo);
    if (filters.status)
      ft = ft.filter((t) => String(t.status) === filters.status);
    this.setState({ filteredTasks: ft });
  };

  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({
      currentTask: {
        ...prev.currentTask,
        [name]:
          name === "estimatedHours" || name === "actualHours"
            ? parseInt(value || 0)
            : value,
      },
      errors: { ...prev.errors, [name]: undefined }, // ✅ clear error for this field
    }));
  };

  handleSave = () => {
    const { currentTask } = this.state;
    const api = process.env.REACT_APP_API;

    const errors = this.validateTask(currentTask);
    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
      return;
    }

    // ✅ Clean payload
    const payload = {
      projectId: currentTask.projectId || null,
      storeId: this.context.storeId,
      created: currentTask.created || null,
      taskName: currentTask.taskName.trim(),
      taskDescription: currentTask.taskDescription || "",
      createdBy: currentTask.createdBy || null,
      assignedTo: currentTask.assignedTo || null,
      startDate: currentTask.startDate
        ? new Date(currentTask.startDate).toISOString()
        : null,
      endDate: currentTask.endDate
        ? new Date(currentTask.endDate).toISOString()
        : null,
      estimatedHours: currentTask.estimatedHours
        ? Number(currentTask.estimatedHours)
        : null,
      actualHours: currentTask.actualHours
        ? Number(currentTask.actualHours)
        : null,
      status: currentTask.status != null ? Number(currentTask.status) : 0,
    };

    if (currentTask.id) {
      payload.id = currentTask.id;
      axios
        .put(`${api}ProjectTask/${currentTask.id}`, payload)
        .then(() => {
          this.loadData();
          this.handleClose();
        })
        .catch((err) => {
          console.error("Update failed:", err.response?.data || err);
          alert("Failed to update task");
        });
    } else {
      axios
        .post(`${api}ProjectTask`, payload)
        .then(() => {
          this.loadData();
          this.handleClose();
        })
        .catch((err) => {
          console.error("Add failed:", err.response?.data || err);
          alert("Failed to add task");
        });
    }
  };

  // ✅ Centralized Validation
  validateTask = (task) => {
    const errors = {};

    if (!task.taskName || task.taskName.trim() === "") {
      errors.taskName = "Task Name is required";
    }

    if (!task.projectId) {
      errors.projectId = "Please select a Project";
    }

    if (!task.assignedTo) {
      errors.assignedTo = "Please select an Employee";
    }

    if (task.startDate && task.endDate) {
      const start = new Date(task.startDate);
      const end = new Date(task.endDate);
      if (end < start) {
        errors.endDate = "End Date cannot be earlier than Start Date";
      }
    }

    if (task.estimatedHours < 0) {
      errors.estimatedHours = "Estimated Hours must be positive";
    }

    if (task.actualHours < 0) {
      errors.actualHours = "Actual Hours must be positive";
    }

    return errors;
  };

  handleEdit = (task) => {
    this.setState({
      currentTask: { ...this.getEmptyTask(), ...task },
      showModal: true,
      errors: {},
    });
  };

  handleDelete = (id) => {
    if (window.confirm("Delete this task?")) {
      const api = process.env.REACT_APP_API;
      axios.delete(`${api}ProjectTask/${id}`).then(this.loadData);
    }
  };
  handleShow = (task = this.getEmptyTask()) => {
    this.setState({ showModal: true, currentTask: task, errors: {} });
  };

  handleClose = () => {
    this.setState({
      showModal: false,
      currentTask: this.getEmptyTask(),
      errors: {},
    });
  };
  // Look up project name by id
  getProjectName = (projectId) => {
    if (!projectId) return "—";
    const { projects } = this.state;
    const idStr = String(projectId);
    const p = projects.find((x) => String(x.id) === idStr);
    return p?.projectName || "—";
  };

  // Look up employee full name by id
  getEmployeeName = (employeeId) => {
    if (!employeeId) return "—";
    const { employees } = this.state;
    const idStr = String(employeeId);
    const e = employees.find((x) => String(x.id) === idStr);
    return e ? `${e.firstName} ${e.lastName}` : "—";
  };

  // Small date helper
  formatDate = (value) => {
    if (!value) return "";
    const d = typeof value === "string" ? new Date(value) : value;
    if (isNaN(d)) return "";
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  render() {
    const {
      projects,
      employees,
      filteredTasks,
      showModal,
      currentTask,
      filters,
      currentPage,
      pageSize,
    } = this.state;

    const statusOptions = [
      { value: 0, label: "Not Started", color: "secondary" },
      { value: 1, label: "In Progress", color: "info" },
      { value: 2, label: "Completed", color: "success" },
      { value: 3, label: "On Hold", color: "warning" },
      { value: 4, label: "Cancelled", color: "danger" },
    ];

    const indexOfLast = currentPage * pageSize;
    const indexOfFirst = indexOfLast - pageSize;
    const currentTasks = filteredTasks.slice(indexOfFirst, indexOfLast);
    const totalPages = Math.ceil(filteredTasks.length / pageSize);

    return (
      <div className="container mt-0">
        <h3 className="shadow">
          <b>Project Tasks Management</b>
        </h3>

        {/* Filters */}
        <Row className="mb-3 mt-3">
          <Col>
            <Form.Select
              name="projectId"
              value={filters.projectId}
              onChange={this.handleFilterChange}
            >
              <option value="">All Projects</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.projectName}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col>
            <Form.Select
              name="assignedTo"
              value={filters.assignedTo}
              onChange={this.handleFilterChange}
            >
              <option value="">All Employees</option>
              {employees.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.firstName} {e.lastName}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col>
            <Form.Select
              name="status"
              value={filters.status}
              onChange={this.handleFilterChange}
            >
              <option value="">All Status</option>
              {statusOptions.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </Form.Select>
          </Col>
        </Row>

        <Button
          className="mb-3"
          onClick={() =>
            this.setState({ showModal: true, currentTask: this.getEmptyTask() })
          }
        >
          Add Task
        </Button>

        {/* Table */}
        <Table striped bordered hover>
          <thead>
            <tr>
              <th>#</th>
              <th>Task Name</th>
              <th>Project</th>
              <th>Assigned To</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Est. Hours</th>
              <th>Actual Hours</th>
              <th>Status</th>
              <th>Created</th> {/* ➕ Added */}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentTasks.map((task, idx) => (
              <tr key={task.id}>
                <td>{(currentPage - 1) * pageSize + idx + 1}</td>
                <td>{task.taskName}</td>
                <td>{this.getProjectName(task.projectId)}</td>
                <td>{this.getEmployeeName(task.assignedTo)}</td>
                <td>{task.startDate ? task.startDate.split("T")[0] : ""}</td>
                <td>{task.endDate ? task.endDate.split("T")[0] : ""}</td>
                <td>{task.estimatedHours}</td>
                <td>{task.actualHours}</td>
                <td>
                  <Badge
                    bg={
                      task.status === 1
                        ? "warning"
                        : task.status === 2
                        ? "info"
                        : task.status === 3
                        ? "success"
                        : "secondary"
                    }
                  >
                    {
                      ["Not Started", "In Progress", "Completed", "On Hold"][
                        task.status
                      ]
                    }
                  </Badge>
                </td>
                <td>{this.formatDate(task.created)}</td> {/* ➕ Added */}
                <td>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => this.handleEdit(task)}
                  >
                    Edit
                  </Button>{" "}
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => this.handleDelete(task.id)}
                  >
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {/* Pagination */}
        <div className="d-flex justify-content-between">
          <span>
            Page {currentPage} of {totalPages}
          </span>
          <div>
            <Button
              size="sm"
              disabled={currentPage === 1}
              onClick={() => this.setState({ currentPage: currentPage - 1 })}
            >
              Prev
            </Button>{" "}
            <Button
              size="sm"
              disabled={currentPage === totalPages}
              onClick={() => this.setState({ currentPage: currentPage + 1 })}
            >
              Next
            </Button>
          </div>
        </div>

        {/* Modal */}
        <Modal show={this.state.showModal} onHide={this.handleClose} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {this.state.currentTask.id ? "Edit Task" : "Add Task"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Row>
                {/* Project Dropdown */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Project</Form.Label>
                    <Form.Select
                      name="projectId"
                      value={this.state.currentTask.projectId || ""}
                      onChange={this.handleChange}
                      isInvalid={!!this.state.errors.projectId}
                    >
                      <option value="">-- Select Project --</option>
                      {this.state.projects.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.projectName}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {this.state.errors.projectId}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {/* Assigned To */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Assigned To</Form.Label>
                    <Form.Select
                      name="assignedTo"
                      value={this.state.currentTask.assignedTo || ""}
                      onChange={this.handleChange}
                      isInvalid={!!this.state.errors.assignedTo}
                    >
                      <option value="">-- Select Employee --</option>
                      {this.state.employees.map((e) => (
                        <option key={e.id} value={e.id}>
                          {e.firstName} {e.lastName}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {this.state.errors.assignedTo}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                {/* Task Name */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Task Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="taskName"
                      value={this.state.currentTask.taskName || ""}
                      onChange={this.handleChange}
                      isInvalid={!!this.state.errors.taskName}
                    />
                    <Form.Control.Feedback type="invalid">
                      {this.state.errors.taskName}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {/* Status */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      name="status"
                      value={this.state.currentTask.status || 0}
                      onChange={this.handleChange}
                    >
                      <option value="0">Not Started</option>
                      <option value="1">In Progress</option>
                      <option value="2">Completed</option>
                      <option value="3">On Hold</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                {/* Start Date */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="startDate"
                      value={
                        this.state.currentTask.startDate
                          ? this.state.currentTask.startDate.split("T")[0]
                          : ""
                      }
                      onChange={this.handleChange}
                    />
                  </Form.Group>
                </Col>

                {/* End Date */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="endDate"
                      value={
                        this.state.currentTask.endDate
                          ? this.state.currentTask.endDate.split("T")[0]
                          : ""
                      }
                      onChange={this.handleChange}
                      isInvalid={!!this.state.errors.endDate}
                    />
                    <Form.Control.Feedback type="invalid">
                      {this.state.errors.endDate}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                {/* Estimated Hours */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Estimated Hours</Form.Label>
                    <Form.Control
                      type="number"
                      name="estimatedHours"
                      value={this.state.currentTask.estimatedHours || ""}
                      onChange={this.handleChange}
                      isInvalid={!!this.state.errors.estimatedHours}
                    />
                    <Form.Control.Feedback type="invalid">
                      {this.state.errors.estimatedHours}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {/* Actual Hours */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Actual Hours</Form.Label>
                    <Form.Control
                      type="number"
                      name="actualHours"
                      value={this.state.currentTask.actualHours || ""}
                      onChange={this.handleChange}
                      isInvalid={!!this.state.errors.actualHours}
                    />
                    <Form.Control.Feedback type="invalid">
                      {this.state.errors.actualHours}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              {/* Task Description - full row */}
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Task Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={10}
                      name="taskDescription"
                      value={this.state.currentTask.taskDescription || ""}
                      onChange={this.handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.handleClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={this.handleSave}>
              Save
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}
